import React, { useState, useEffect, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import CampaignPicker from './CampaignPicker.js';
import { smGet, smPost, smPut, errorText, personName } from './api.js';

// Same as DEFAULT_HOOK in src/server/salesMachine/ai.js.
const DEFAULT_HOOK = 'you\'re heading {area} at {company}';

const MESSAGE_STATUS = {
    none: { label: 'No draft', color: 'default' },
    draft: { label: 'To review', color: 'warning' },
    approved: { label: 'Approved', color: 'success' },
    skipped: { label: 'Skipped', color: 'default' },
};

function ContentTab({ campaignId, onCampaign, onLaunch }) {
    const [data, setData] = useState(null);
    const [instructions, setInstructions] = useState('');
    const [steps, setSteps] = useState([]);
    const [sender, setSender] = useState({ signature: '', unsubscribeLine: '', maxWords: '', defaultHook: '' });
    const [selected, setSelected] = useState(null);
    const [edit, setEdit] = useState(null);
    const [progress, setProgress] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [includeApproved, setIncludeApproved] = useState(false);
    const [change, setChange] = useState('');
    const [addToInstructions, setAddToInstructions] = useState(true);
    const [confirm, setConfirm] = useState(null); // 'redraft' | 'change'

    const load = useCallback(async () => {
        if (!campaignId) { setData(null); return; }
        try {
            const d = await smGet(`/campaigns/${campaignId}`);
            setData(d);
            setInstructions(d.campaign.instructions || '');
            setSteps(d.campaign.steps || []);
            setSender({ signature: d.campaign.signature || '', unsubscribeLine: d.campaign.unsubscribeLine || '', maxWords: d.campaign.maxWords ? String(d.campaign.maxWords) : '', defaultHook: d.campaign.defaultHook ?? DEFAULT_HOOK });
        } catch (e) { setError(errorText(e)); }
    }, [campaignId]);
    useEffect(() => { load(); setSelected(null); }, [load]);

    const campaign = data?.campaign;
    const messages = campaign?.messages || {};
    const statusOf = id => (messages[id] ? (messages[id].status || 'draft') : 'none');
    const counts = useMemo(() => {
        const c = { all: 0, none: 0, draft: 0, approved: 0, skipped: 0 };
        (campaign?.contacts || []).forEach(p => { c.all++; c[statusOf(p.id)]++; });
        return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign]);
    const people = (campaign?.contacts || []).filter(p => filter === 'all' || statusOf(p.id) === filter);
    const draftMode = campaign?.status === 'draft';

    useEffect(() => {
        const m = selected && messages[selected];
        setEdit(m ? { subject: m.subject || '', steps: [...(m.steps || [])] } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, campaign]);

    const run = async (label, fn) => {
        setBusy(label); setError(null);
        try { await fn(); } catch (e) { setError(errorText(e)); }
        setBusy(null);
    };

    const instructionsDirty = campaign && (instructions !== (campaign.instructions || '') || JSON.stringify(steps) !== JSON.stringify(campaign.steps || [])
        || sender.signature !== (campaign.signature || '') || sender.unsubscribeLine !== (campaign.unsubscribeLine || '') || sender.maxWords !== (campaign.maxWords ? String(campaign.maxWords) : '') || sender.defaultHook !== (campaign.defaultHook ?? DEFAULT_HOOK));
    const saveInstructions = () => run('instructions', async () => {
        await smPut(`/campaigns/${campaignId}`, { instructions, ...sender, ...(draftMode ? { steps } : {}) });
        await load();
    });

    // Drafts in groups of 5 so the page can show progress and a slow website doesn't hold up the rest.
    const draft = ids => run('draft', async () => {
        if (instructionsDirty) await smPut(`/campaigns/${campaignId}`, { instructions, ...sender, ...(draftMode ? { steps } : {}) });
        setProgress({ done: 0, total: ids.length, label: 'Drafted' });
        for (let i = 0; i < ids.length; i += 5) {
            await smPost(`/campaigns/${campaignId}/generate`, { contactIds: ids.slice(i, i + 5) });
            setProgress({ done: Math.min(i + 5, ids.length), total: ids.length, label: 'Drafted' });
        }
        setProgress(null);
        await load();
    });

    // Who "apply to all" works on: the drafts to review, and the approved ones only when asked.
    // Skipped people are left alone.
    const targets = (campaign?.contacts || []).filter(p => statusOf(p.id) === 'draft' || (includeApproved && statusOf(p.id) === 'approved')).map(p => p.id);
    const redraftAll = () => { setConfirm(null); draft([...targets, ...(campaign.contacts || []).filter(p => statusOf(p.id) === 'none').map(p => p.id)]); };

    // One change in plain words, applied by Claude to every draft in groups of 5; the rest of each
    // draft stays as it is. Optionally kept in the instructions, so future drafts follow it too.
    const changeAll = () => run('change', async () => {
        setConfirm(null);
        const text = change.trim();
        if (addToInstructions) {
            const nextInstructions = `${instructions.trim()}\n\n${text}`;
            await smPut(`/campaigns/${campaignId}`, { instructions: nextInstructions, ...sender, ...(draftMode ? { steps } : {}) });
            setInstructions(nextInstructions);
        }
        setProgress({ done: 0, total: targets.length, label: 'Changed' });
        for (let i = 0; i < targets.length; i += 5) {
            await smPost(`/campaigns/${campaignId}/revise`, { change: text, contactIds: targets.slice(i, i + 5) });
            setProgress({ done: Math.min(i + 5, targets.length), total: targets.length, label: 'Changed' });
        }
        setProgress(null);
        setChange('');
        await load();
    });

    const saveMessage = status => run('save', async () => {
        await smPut(`/campaigns/${campaignId}/messages/${selected}`, { ...edit, ...(status ? { status } : {}) });
        await load();
        if (status) {
            // On to the next person still to review.
            const next = (campaign.contacts || []).find(p => p.id !== selected && statusOf(p.id) === 'draft');
            if (next) setSelected(next.id);
        }
    });

    const bulkStatus = (ids, status) => run('bulk', async () => {
        await smPost(`/campaigns/${campaignId}/status`, { contactIds: ids, status });
        await load();
    });

    const person = selected && (campaign?.contacts || []).find(p => p.id === selected);
    const msg = selected && messages[selected];

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <CampaignPicker value={campaignId} onChange={onCampaign} />
                {campaign && <Chip label={campaign.status} />}
                <Box sx={{ flex: 1 }} />
                {campaign && <Button variant="contained" onClick={onLaunch} disabled={!counts.approved}>Next: launch ({counts.approved} approved)</Button>}
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!campaign && <Typography color="text.secondary">Create a campaign from an audience on the Audience tab.</Typography>}
            {campaign && (
                <>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Instructions for the messages</Typography>
                        <TextField multiline minRows={6} maxRows={18} fullWidth value={instructions} onChange={e => setInstructions(e.target.value)}
                            helperText="What Monada offers, the angle for each role, the tone, the length, the language. Claude follows these for every person, using facts from Apollo and the company's website." />
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Emails in the sequence</Typography>
                        {steps.map((s, i) => (
                            <Box key={s.id || i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                <TextField size="small" label={`Email ${i + 1}`} value={s.label} disabled={!draftMode} onChange={e => setSteps(st => st.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} />
                                {i > 0 && <TextField size="small" type="number" label="Days after the previous one" value={s.delayDays} disabled={!draftMode} sx={{ width: 200 }} onChange={e => setSteps(st => st.map((x, k) => (k === i ? { ...x, delayDays: e.target.value } : x)))} />}
                                {i > 0 && <Typography variant="caption" color="text.secondary">if there is no reply</Typography>}
                                {draftMode && steps.length > 1 && i === steps.length - 1 && <IconButton size="small" onClick={() => setSteps(st => st.slice(0, -1))}>✕</IconButton>}
                            </Box>
                        ))}
                        {draftMode && steps.length < 6 && <Button size="small" onClick={() => setSteps(st => [...st, { id: `step-${st.length + 1}`, label: `Follow-up ${st.length}`, delayDays: 5 }])}>Add a follow-up</Button>}
                        {!draftMode && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>The steps are fixed once the campaign is launched.</Typography>}
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Signature and opt-out line for this campaign</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
                            <TextField multiline minRows={2} size="small" label="Signature" value={sender.signature} onChange={e => setSender(s => ({ ...s, signature: e.target.value }))}
                                helperText="Leave empty to use the one in Settings." />
                            <TextField multiline minRows={2} size="small" label="Opt-out line" value={sender.unsubscribeLine} onChange={e => setSender(s => ({ ...s, unsubscribeLine: e.target.value }))}
                                helperText="Added to the end of every email. Use the campaign's language. Leave empty to use the one in Settings." />
                        </Box>
                        <TextField size="small" type="number" label="Word limit for the first email" value={sender.maxWords} onChange={e => setSender(s => ({ ...s, maxWords: e.target.value }))} sx={{ mt: 2, width: 260 }}
                            helperText="Not counting the signature. A draft over it is shortened automatically. Empty for no limit." />
                        <TextField size="small" fullWidth label="Opening when no recent event is found" value={sender.defaultHook} onChange={e => setSender(s => ({ ...s, defaultHook: e.target.value }))} sx={{ mt: 2 }}
                            helperText={'Used in place of the hook, e.g. "I noticed that you\'re heading logistics at Kalle." Fields: {area} (logistics, finance, IT…), {company}, {title}, {firstName}. Empty means [ADD HOOK], to write by hand.'} />
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button variant="outlined" onClick={saveInstructions} disabled={!instructionsDirty || !!busy}>Save instructions</Button>
                            <Button variant="contained" onClick={() => draft((campaign.contacts || []).filter(p => statusOf(p.id) === 'none').map(p => p.id))} disabled={!!busy || !counts.none}>
                                Draft messages for {counts.none} {counts.none === 1 ? 'person' : 'people'} without one
                            </Button>
                        </Box>
                        {progress && <Box sx={{ mt: 2 }}><LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} /><Typography variant="caption">{progress.label} {progress.done} of {progress.total}…</Typography></Box>}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2">Apply to every draft</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Works on the {counts.draft} drafts to review{includeApproved ? ` and the ${counts.approved} approved` : ''}. Skipped people are left alone. A changed approved message goes back to review.
                        </Typography>
                        <FormControlLabel control={<Checkbox size="small" checked={includeApproved} onChange={e => setIncludeApproved(e.target.checked)} />} label={`Include the ${counts.approved} approved messages`} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                            <TextField size="small" fullWidth multiline maxRows={4} value={change} onChange={e => setChange(e.target.value)}
                                label="A change to make in every draft"
                                placeholder={'e.g. "Add a P.S. that we\'ll be at Fruit Logistica in February" or "Say \'freight invoices\' instead of \'invoices\'"'} />
                            <Button variant="contained" onClick={() => setConfirm('change')} disabled={!!busy || !change.trim() || !targets.length} sx={{ whiteSpace: 'nowrap' }}>Change {targets.length} drafts</Button>
                        </Box>
                        <FormControlLabel control={<Checkbox size="small" checked={addToInstructions} onChange={e => setAddToInstructions(e.target.checked)} />} label="Also add it to the instructions, so new drafts follow it" />
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button variant="outlined" onClick={() => setConfirm('redraft')} disabled={!!busy || !(targets.length + counts.none)}>Redraft {targets.length + counts.none} from the instructions</Button>
                            <Typography variant="caption" color="text.secondary">Rewrites them from scratch with the saved instructions. Hand edits in those drafts are lost.</Typography>
                        </Box>
                    </Paper>

                    <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
                        <DialogTitle>{confirm === 'redraft' ? `Redraft ${targets.length + counts.none} people's emails?` : `Change ${targets.length} drafts?`}</DialogTitle>
                        <DialogContent>
                            <Typography>
                                {confirm === 'redraft'
                                    ? 'Each person\'s emails are written again from the instructions and their company\'s website. Any edits you made to these drafts are replaced. It takes about a minute for every 5 people.'
                                    : `Claude applies "${change.trim()}" to each draft and keeps the rest as it is. It takes about half a minute for every 5 people.`}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setConfirm(null)}>Cancel</Button>
                            <Button variant="contained" onClick={confirm === 'redraft' ? redraftAll : changeAll}>{confirm === 'redraft' ? 'Redraft' : 'Change them'}</Button>
                        </DialogActions>
                    </Dialog>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Paper variant="outlined" sx={{ width: 320, flexShrink: 0 }}>
                            <Box sx={{ p: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {['all', 'draft', 'approved', 'none', 'skipped'].map(k => (
                                    <Chip key={k} size="small" label={`${k === 'all' ? 'All' : MESSAGE_STATUS[k].label} (${counts[k]})`} color={filter === k ? 'primary' : 'default'} onClick={() => setFilter(k)} />
                                ))}
                            </Box>
                            {/* Always visible: approves every draft to review, whatever the filter (the server skips
                                empty emails and any still marked [ADD HOOK]). */}
                            {counts.draft > 0 && (
                                <Box sx={{ px: 1, pb: 1 }}>
                                    <Button size="small" variant="outlined" color="success" onClick={() => bulkStatus((campaign.contacts || []).filter(p => statusOf(p.id) === 'draft').map(p => p.id), 'approved')} disabled={!!busy}>
                                        Approve all {counts.draft} to review
                                    </Button>
                                </Box>
                            )}
                            <Divider />
                            <List dense disablePadding sx={{ maxHeight: 640, overflow: 'auto' }}>
                                {people.map(p => {
                                    const s = MESSAGE_STATUS[statusOf(p.id)];
                                    return (
                                        <ListItemButton key={p.id} selected={selected === p.id} onClick={() => setSelected(p.id)}>
                                            <ListItemText primary={personName(p)} secondary={`${p.title} · ${p.company}`} />
                                            <Chip size="small" label={messages[p.id]?.error && statusOf(p.id) !== 'approved' ? 'Error' : s.label} color={messages[p.id]?.error ? 'error' : s.color} variant="outlined" />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Paper>

                        <Paper variant="outlined" sx={{ flex: 1, p: 2, minWidth: 0 }}>
                            {!person && <Typography color="text.secondary">Choose a person to review their messages.</Typography>}
                            {person && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{personName(person)}</Typography>
                                        <Typography variant="body2" color="text.secondary">{person.title} · {person.company} · {person.country} · {person.email}</Typography>
                                    </Box>
                                    {msg?.error && <Alert severity="error" sx={{ mb: 1 }}>The last draft failed: {msg.error}</Alert>}
                                    {!msg && <Button variant="outlined" onClick={() => draft([person.id])} disabled={!!busy}>Draft this person's messages</Button>}
                                    {edit && (
                                        <>
                                            <TextField label="Subject" size="small" fullWidth value={edit.subject} onChange={e => setEdit(x => ({ ...x, subject: e.target.value }))} sx={{ mb: 2 }} />
                                            {(campaign.steps || []).map((s, i) => (
                                                <TextField key={i} label={`${s.label}${i ? ` (day +${s.delayDays}, a reply in the same thread)` : ''}`} multiline minRows={i ? 3 : 6} fullWidth
                                                    value={edit.steps[i] || ''} onChange={e => setEdit(x => ({ ...x, steps: x.steps.map((v, k) => (k === i ? e.target.value : v)) }))} sx={{ mb: 2 }} />
                                            ))}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>The signature and opt-out line (this campaign's own, or the ones in Settings) are added to every email when it is sent.</Typography>
                                            {msg?.hook?.text && (
                                                <Alert severity={msg.hook.isDefault ? 'info' : 'success'} icon={false} sx={{ mb: 2 }}>
                                                    <b>{msg.hook.isDefault ? 'Default opening' : 'Hook'}:</b> {msg.hook.text}
                                                    <Typography variant="caption" component="div" color="text.secondary">
                                                        {msg.hook.date ? `${msg.hook.date} · ` : ''}{String(msg.hook.source || '').replace(/,?\s*https?:\/\/\S+/, '')}
                                                        {(String(msg.hook.source || '').match(/https?:\/\/\S+/) || [])[0] && <> · <a href={String(msg.hook.source).match(/https?:\/\/\S+/)[0]} target="_blank" rel="noreferrer">open the source</a></>}
                                                    </Typography>
                                                </Alert>
                                            )}
                                            {msg && [msg.subject, ...(msg.steps || [])].some(s => String(s || '').includes('[ADD HOOK]')) && (
                                                <Alert severity="warning" sx={{ mb: 2 }}>No recent event was found for this company. Replace [ADD HOOK] in the email with one (e.g. a mutual connection, a new site, a recent post), then approve it.</Alert>
                                            )}
                                            {msg?.revisions?.length > 0 && (
                                                <Alert severity="info" sx={{ mb: 2 }}>
                                                    Last change: "{msg.revisions[msg.revisions.length - 1].change}". {msg.revisions[msg.revisions.length - 1].note}
                                                </Alert>
                                            )}
                                            {msg?.facts?.length > 0 && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Facts used</Typography>
                                                    {msg.facts.map((f, i) => <Typography key={i} variant="body2">· {f.fact} <Typography component="span" variant="caption" color="text.secondary">({f.source})</Typography></Typography>)}
                                                </Box>
                                            )}
                                            {msg?.assumptions?.length > 0 && (
                                                <Alert severity="warning" sx={{ mb: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Check these assumptions</Typography>
                                                    {msg.assumptions.map((a, i) => <Typography key={i} variant="body2">· {a}</Typography>)}
                                                </Alert>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button variant="contained" color="success" onClick={() => saveMessage('approved')} disabled={!!busy || edit.steps.some(s => !String(s || '').trim()) || !edit.subject.trim()}>Approve</Button>
                                                <Button variant="outlined" onClick={() => saveMessage(null)} disabled={!!busy}>Save</Button>
                                                <Button onClick={() => saveMessage('skipped')} disabled={!!busy}>Skip this person</Button>
                                                <Box sx={{ flex: 1 }} />
                                                <Button onClick={() => draft([person.id])} disabled={!!busy}>Redraft</Button>
                                            </Box>
                                        </>
                                    )}
                                </>
                            )}
                        </Paper>
                    </Box>
                </>
            )}
        </Box>
    );
}

export default ContentTab;
